import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {GuidesEnum, HealthGuideServicesTypeEnum, QueryParamsEnum} from 'presentation/constants';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useSearchProcedureQuery} from 'presentation/modules/booking/shared/services/useSearchProcedureQuery';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useDebounce} from 'presentation/pages/private/hooks/useDebounce';

import {useQueryParams} from 'presentation/pages/private/hooks';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {getUserInfoFromToken} from 'presentation/utils';
import {useNavigate} from 'react-router-dom';
import {minChars} from '../../constants';
import {translations} from 'presentation/translations-new';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';

const {emptyState, notFound} =
	translations['pt-br'].privateFlow.bookingFlow.examSelection.procedureSelection;

export function useProcedureSelection() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const {SEARCH_PARAMS} = useQueryParams();
	const {query} = SEARCH_PARAMS;

	const cdPessoaC = getUserInfoFromToken().cdPessoa;

	const bookingData = useBookingStore(state => state.bookingData);
	const setLocation = useLocationStore(state => state.setLocation);

	const debouncedSearch = useDebounce({value: query, delay: 600});

	const {data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError} =
		useSearchProcedureQuery({debouncedSearch, minChars});

	const handleRefresh = () => {
		queryClient.resetQueries({
			queryKey: [QUERY_KEYS.SEARCH_PROCEDURE_GUIDE],
		});
		refetch();
	};

	const procedures = data?.pages.flatMap(page => page?.content ?? []) ?? [];

	const handleGoBack = () => {
		navigate(-1);
	};

	const handleGoToGuideFlow = (nmProcedimento: string, cdProcedimento: string) => {
		setLocation(cdPessoaC, FlowType.HEALTH_GUIDE, {
			state: {
				cdUf: bookingData?.ufCode,
				nmUf: bookingData?.ufName,
			},
			city: bookingData?.cityName,
			districtNames: [],
		});

		const queryParams = new URLSearchParams({
			[QueryParamsEnum.COMING_FROM]: FlowEnum.EXAM,
			[QueryParamsEnum.STEP]: GuideStep.RESULTS,
			[QueryParamsEnum.UF_CODE]: bookingData?.ufCode,
			[QueryParamsEnum.CITY_NAME]: bookingData?.cityName,
			[QueryParamsEnum.SERVICE_TYPE]: HealthGuideServicesTypeEnum.AUXILIARY_SERVICES,
			[QueryParamsEnum.SPECIALTY_NAME]: nmProcedimento,
			[QueryParamsEnum.PROCEDURE]: cdProcedimento,
		});

		if (bookingData?.coordinates) {
			queryParams.append(QueryParamsEnum.COORDINATES, bookingData.coordinates);
		}

		const path = `${GuidesEnum.SERVICE_NETWORK_HEALTH}?${queryParams.toString()}`;

		navigate(path, {replace: true});
	};

	const showEmptyState = procedures.length === 0 && !isError && !isLoading;
	const emptyContent = query.length === 0 ? emptyState : notFound;

	return {
		procedures,
		isLoading,
		isError,
		isFetchingNextPage,
		hasNextPage,
		handleRefresh,
		handleGoBack,
		handleGoToGuideFlow,
		fetchNextPage,
		showEmptyState,
		query,
		emptyContent,
	};
}
